// src/runtime/RuntimeRegistry.ts
// ============================================
// RuntimeRegistry - Runtime 注册与管理（冻结版本 v1.0）
// 
// 职责：
//   - 注册/注销 Runtime
//   - 提供只读访问接口
//   - 不暴露内部 Map
//   - 不包含日志逻辑（由外部 Logger 注入）
// ============================================

import { IRuntime, RuntimeName, RuntimeInfo } from '../types/runtime';

// ============================================
// Logger 接口（依赖注入，便于测试）
// ============================================
export interface Logger {
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
}

export class RuntimeRegistry {
  private runtimes: Map<RuntimeName, IRuntime> = new Map();
  private logger?: Logger;

  constructor(logger?: Logger) {
    this.logger = logger;
  }

  /**
   * 注入 Logger（便于测试）
   */
  setLogger(logger: Logger): void {
    this.logger = logger;
  }

  // ============================================
  // 核心操作
  // ============================================

  /**
   * 注册 Runtime
   * 如果已存在则抛出异常（显式错误，而非静默跳过）
   */
 register(runtime: IRuntime): void {
  if (this.runtimes.has(runtime.name)) {
    this.logger?.warn(
      `[RuntimeRegistry] Runtime "${runtime.name}" already registered, skipping (likely React StrictMode double-invoke)`
    );
    return; // 不抛异常，静默跳过
  }
  this.runtimes.set(runtime.name, runtime);
  this.logger?.info(`[RuntimeRegistry] Registered ${runtime.name}`);
}

  /**
   * 注销 Runtime
   */
  unregister(name: RuntimeName): void {
    if (!this.runtimes.has(name)) {
      this.logger?.warn(`[RuntimeRegistry] Runtime "${name}" not found, skipping unregister`);
      return;
    }
    this.runtimes.delete(name);
    this.logger?.info(`[RuntimeRegistry] Unregistered ${name}`);
  }

  /**
   * 获取 Runtime（只读）
   */
  get<T extends IRuntime>(name: RuntimeName): T {
    const runtime = this.runtimes.get(name);
    if (!runtime) {
      throw new Error(`[RuntimeRegistry] Runtime "${name}" not found`);
    }
    return runtime as T;
  }

  /**
   * 检查 Runtime 是否存在
   */
  has(name: RuntimeName): boolean {
    return this.runtimes.has(name);
  }

  // ============================================
  // 只读迭代器（不暴露内部 Map）
  // ============================================

  /**
   * 获取所有 Runtime 的值（只读数组）
   */
  values(): IRuntime[] {
    return Array.from(this.runtimes.values());
  }

  /**
   * 获取所有 Runtime 的条目（只读数组）
   */
  entries(): [RuntimeName, IRuntime][] {
    return Array.from(this.runtimes.entries());
  }

  /**
   * 遍历所有 Runtime
   */
  forEach(callback: (runtime: IRuntime, name: RuntimeName) => void): void {
    this.runtimes.forEach(callback);
  }

  /**
   * 获取所有 Runtime 名称（只读数组）
   */
  getNames(): RuntimeName[] {
    return Array.from(this.runtimes.keys());
  }

  /**
   * 获取 Runtime 信息列表（便于 Dashboard 展示）
   */
  list(): RuntimeInfo[] {
    const result: RuntimeInfo[] = [];
    for (const [, runtime] of this.runtimes) {
      try {
        result.push(runtime.getInfo());
      } catch (error) {
        this.logger?.error(
          `[RuntimeRegistry] Failed to get info for ${runtime.name}:`,
          error
        );
      }
    }
    return result;
  }

  // ============================================
  // 属性
  // ============================================

  /**
   * Runtime 数量（只读）
   */
  get size(): number {
    return this.runtimes.size;
  }

  /**
   * 检查是否为空
   */
  get isEmpty(): boolean {
    return this.runtimes.size === 0;
  }

  // ============================================
  // 生命周期
  // ============================================

  /**
   * 清空所有 Runtime（仅用于测试）
   */
  clear(): void {
    this.runtimes.clear();
    this.logger?.debug('[RuntimeRegistry] Cleared');
  }

  /**
   * 销毁 Registry（释放所有资源）
   */
  destroy(): void {
    // 尝试销毁所有 Runtime
    for (const [name, runtime] of this.runtimes) {
      try {
        if (typeof (runtime as any).destroy === 'function') {
          (runtime as any).destroy();
        }
      } catch (error) {
        this.logger?.error(
          `[RuntimeRegistry] Failed to destroy ${name}:`,
          error
        );
      }
    }
    this.runtimes.clear();
    this.logger = undefined;
  }
}

export default RuntimeRegistry;